import React, { useEffect, useState } from 'react'
import { Tooltip, ClickAwayListener, Checkbox, FormControlLabel, Alert } from '@mui/material'
import {
  LocationOnOutlined as LocationOnOutlinedIcon,
  EventOutlined as EventOutlinedIcon,
  StarBorder as StarBorderIcon,
} from '@material-ui/icons';

import { StyledButton } from '../Buttons/DefaultButton/DefaultButton.styles';
import { HygienistBackendJob } from "components/EditJob";
import { JobInfo } from "./JobInfo";

import {
  ButtonContainer,
  Card,
  ContentContainer,
  CoverLetterRow,
  HeaderCardInner,
  HeaderIllustrationWrap,
  HeaderMetaRow,
  HeaderPracticeRow,
  HeaderRole,
  HeaderTextStack,
  HiringPill,
} from "./HygienistJobDetails.styles";

interface HygienistJobDetailsProps {
  job: HygienistBackendJob
  includeCoverLetter: boolean,
  onIncludeCoverLetterChange: (checked: boolean) => void,
  onApply: () => void
  onWithdraw: () => void
  isExpired?: boolean
}

// T2-6045: Tooth illustration (cobalt outline). Inline so we don't ship a new
// asset to src/images/. Sized to fit inside the 80x80 circular badge.
const ToothIllustration: React.FC = () => (
  <svg viewBox="0 0 64 64" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M32 6c-7 0-13 3-17 7-3 3-3 8-2 12 2 7 4 13 4 19 0 4 1 8 4 11 2 2 4 1 5-1 1-3 2-7 3-12 1-3 2-5 3-5s2 2 3 5c1 5 2 9 3 12 1 2 3 3 5 1 3-3 4-7 4-11 0-6 2-12 4-19 1-4 1-9-2-12-4-4-10-7-17-7Z"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinejoin="round"
    />
    <path d="M44 14c2 1 3 3 3 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="50" cy="12" r="1.6" fill="currentColor" />
    <circle cx="56" cy="20" r="1.2" fill="currentColor" />
    <circle cx="14" cy="14" r="1.2" fill="currentColor" />
  </svg>
);

const formatPostedDate = (dateString?: string): string | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const HygienistJobDetails = ({job, includeCoverLetter, onIncludeCoverLetterChange, onApply, onWithdraw, isExpired = false}: HygienistJobDetailsProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const practiceName = job.location?.practiceName || '';
  const isObfuscated = practiceName.includes('*');
  const cityProvince = [job.location?.city, job.location?.province].filter(Boolean).join(', ');
  const postedOn = formatPostedDate(job.createdOn);

  useEffect(() => {
    if (!tooltipOpen) return undefined;

    const closeTooltip = () => setTooltipOpen(false);
    window.addEventListener('scroll', closeTooltip, true);
    document.addEventListener('scroll', closeTooltip, true);

    return () => {
      window.removeEventListener('scroll', closeTooltip, true);
      document.removeEventListener('scroll', closeTooltip, true);
    };
  }, [tooltipOpen]);

  return (
    <ContentContainer>
      <Card>
        <HeaderCardInner>
          <HeaderTextStack>
            <HiringPill>
              <StarBorderIcon style={{ fontSize: 14 }} />
              We&apos;re Hiring
            </HiringPill>
            <HeaderRole>{job.title || 'Dental Professional'}</HeaderRole>
            {practiceName && (
              <HeaderPracticeRow>
                <span>{practiceName}</span>
                {isObfuscated && (
                  <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                    <Tooltip
                      title="Dental offices prefer to keep their practice name private in the postings, but we show you enough of the name to help prevent you from applying to an unwanted office."
                      open={tooltipOpen}
                      disableHoverListener
                      disableFocusListener
                      disableTouchListener
                      placement="bottom"
                    >
                      <button
                        type="button"
                        onClick={() => setTooltipOpen((v) => !v)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          color: 'var(--primary-blue, #0B4598)',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                        }}
                      >
                        What&apos;s this?
                      </button>
                    </Tooltip>
                  </ClickAwayListener>
                )}
              </HeaderPracticeRow>
            )}
            <HeaderMetaRow>
              {cityProvince && (
                <span>
                  <LocationOnOutlinedIcon />
                  {cityProvince}
                </span>
              )}
              {postedOn && (
                <span>
                  <EventOutlinedIcon />
                  Posted on {postedOn}
                </span>
              )}
            </HeaderMetaRow>
          </HeaderTextStack>
          <HeaderIllustrationWrap aria-hidden="true">
            <ToothIllustration />
          </HeaderIllustrationWrap>
        </HeaderCardInner>
      </Card>

      <JobInfo job={job}/>

      {isExpired && (
        <Alert
          severity="warning"
          sx={{
            alignSelf: 'stretch',
            '& .MuiAlert-message': { width: '100%', textAlign: 'center' }
          }}
        >
          This job posting has expired and is no longer accepting applications.
        </Alert>
      )}

      {!job.isApplied && !isExpired && (
        <CoverLetterRow>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeCoverLetter}
                onChange={(e) => onIncludeCoverLetterChange(e.target.checked)}
                name="includeCoverLetter"
                color="primary"
              />
            }
            label={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 600, color: 'rgba(0,0,0,0.85)' }}>Include a Cover Letter</span>
                <span style={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.55)' }}>
                  Adding a cover letter helps you stand out to employers.
                </span>
              </div>
            }
            style={{ margin: 0 }}
          />
        </CoverLetterRow>
      )}

      <ButtonContainer>
        <StyledButton
          variant={job.isApplied ? "outlined" : "contained"}
          onClick={job.isApplied ? onWithdraw : onApply}
          color="primary"
          size="large"
          id="apply-button"
          disabled={isExpired && !job.isApplied}
        >
          {isExpired && !job.isApplied
            ? 'Job Expired'
            : (job.isApplied ? 'Withdraw my application' : 'Apply')
          }
        </StyledButton>
      </ButtonContainer>
    </ContentContainer>
  )
}

export default HygienistJobDetails;
